import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { LogOut, ShieldAlert } from 'lucide-react';
import { safeFetch } from '../../lib/api';
import {
  TEAM_SELECTION_INVALID_EVENT,
  clearStoredTeamSelection,
  getStoredTeamSelection,
  setStoredTeamSelection,
  type EligibleTeam,
  type OrgTeamRole,
} from '../../lib/teamSelection';
import { notifyToast } from '../../lib/toastStore';
import ShellSkeleton from './ShellSkeleton';
import TeamSelectionScreen, { TEAM_ROLE_LABEL } from './TeamSelectionScreen';

/**
 * TeamSelectionGate — multi-team client gate (Slice 2, plan §(a)/(c)).
 *
 * Mounted by App INSIDE each org-role dashboard branch, after the role
 * is chosen and approved and after the onboarding/pending gates — so it
 * only ever runs for a user who is cleared to enter that role's console.
 * It resolves the role's eligible teams from `GET /api/{role}/teams`
 * (the Slice 1 enumeration; the server is the only authority) and:
 *
 *  - exactly 1 team  ⇒ the selection is stored and the dashboard mounts
 *    directly (single-team experience is unchanged — no screen, no step);
 *  - more than 1     ⇒ a stored selection is adopted ONLY if it appears
 *    in the freshly fetched list; otherwise TeamSelectionScreen renders;
 *  - 0 teams         ⇒ the gate does not engage: children render exactly
 *    as before (legacy fall-through — onboarding/pending/not-found
 *    states own those users, never a fabricated picker);
 *  - fetch failure   ⇒ an error state with retry. A failed lookup is
 *    "unknown", never an empty list (the PR-13 / D-9 rule).
 *
 * While `ready`, the dashboard subtree renders under `key={teamId}` so
 * a team change remounts it wholesale — the previous team's component
 * state cannot bleed into the new context. If any org-scoped request
 * later answers 409 TEAM_SELECTION_REQUIRED or 403 TEAM_NOT_MEMBER,
 * safeFetch clears the stored selection and dispatches
 * `team:selection-invalid`; the gate re-resolves and lands the user on
 * the picker (or the remaining team) with a plain-language note —
 * never an error page, never a silent swap.
 *
 * Slice 3: the gate also publishes its resolution through
 * TeamSelectionContext (below) — the in-dashboard TeamSwitcher's single
 * source for the eligible list, the active team, and switching. The
 * `isats_accepted_org_id` fresh-acceptance hint is honored here for
 * ALL roles (Slice 2: employer only), and a confirmed zero-team
 * resolution clears any stale stored selection (Slice 2 verify NB-1).
 */

/**
 * The gate-resolved team context, consumed by the in-dashboard
 * TeamSwitcher (Slice 3, plan §(d)). The gate is the single owner of
 * the eligible list and the active selection: the switcher renders
 * from this context and switches through it, so there is exactly one
 * enumeration and one selection state per role — never a second copy
 * inside a dashboard that could diverge.
 */
export interface TeamSelectionContextValue {
  role: OrgTeamRole;
  /** Eligible teams from the gate's latest resolution (empty in passthrough). */
  teams: EligibleTeam[];
  /** The team the dashboard subtree is mounted under (null in passthrough). */
  activeTeamId: string | null;
  /** Persist the choice and remount the subtree under the new team. */
  switchTeam: (teamId: string) => void;
}

export const TeamSelectionContext = React.createContext<TeamSelectionContextValue | null>(null);

/** The gate-resolved team context, or null outside a gate (standalone mounts). */
export function useTeamSelection(): TeamSelectionContextValue | null {
  return useContext(TeamSelectionContext);
}

type GateState =
  | { phase: 'loading' }
  | { phase: 'error' }
  | { phase: 'passthrough' }
  | { phase: 'ready'; teamId: string }
  | { phase: 'pick'; teams: EligibleTeam[]; note: string | null }
  | { phase: 'blocked'; team: EligibleTeam };

interface TeamSelectionGateProps {
  role: OrgTeamRole;
  onLogout: () => void;
  children: React.ReactNode;
}

function normalizeTeams(data: any): EligibleTeam[] {
  if (!data || !Array.isArray(data.teams)) {
    // A malformed payload is a failed lookup, not a confirmed zero.
    throw new Error('Malformed teams response');
  }
  return data.teams.filter(
    (t: any) => t && typeof t.id === 'string' && t.id,
  ) as EligibleTeam[];
}

export default function TeamSelectionGate({ role, onLogout, children }: TeamSelectionGateProps) {
  const [state, setState] = useState<GateState>({ phase: 'loading' });
  const stateRef = useRef(state);
  stateRef.current = state;
  const teamsRef = useRef<EligibleTeam[]>([]);
  const runIdRef = useRef(0);

  const fetchTeams = useCallback(async (): Promise<EligibleTeam[]> => {
    const data = await safeFetch(`/api/${role}/teams`);
    const teams = normalizeTeams(data);
    teamsRef.current = teams;
    return teams;
  }, [role]);

  /** The 0 / 1 / N adoption logic over a freshly fetched list. */
  const adopt = useCallback(
    (teams: EligibleTeam[]) => {
      if (teams.length === 0) {
        // Slice 3 rider (Slice 2 verification NB-1): a confirmed zero
        // means any stored selection for this role is dead — clear it
        // here so the legacy surfaces below never fire a first call
        // carrying a stale id (it would strict-verify to a 403 and
        // self-heal via the recovery path, but the transient is
        // avoidable).
        clearStoredTeamSelection(role);
        setState({ phase: 'passthrough' });
        return;
      }
      // Fresh-acceptance hint (plan §6; generalized to all roles in
      // Slice 3 — Slice 2 honored it for employers only): the shared
      // accept flow records the just-accepted org in
      // `isats_accepted_org_id` for every org type. When it appears in
      // the fresh list, land in THAT team: it outranks an earlier
      // same-session pick, exactly as EmployerDashboard's own initial
      // pick has always ordered it. The hint is one-shot — consumed on
      // any resolution over a non-empty list (mirroring the dashboard's
      // removeItem semantics) so a later re-resolve can never re-adopt
      // a spent acceptance over the user's current context.
      try {
        const accepted = sessionStorage.getItem('isats_accepted_org_id');
        if (accepted) {
          sessionStorage.removeItem('isats_accepted_org_id');
          if (teams.some((t) => t.id === accepted)) {
            setStoredTeamSelection(role, accepted);
            setState({ phase: 'ready', teamId: accepted });
            return;
          }
        }
      } catch { /* storage unavailable — fall through to stored/picker */ }
      if (teams.length === 1) {
        setStoredTeamSelection(role, teams[0].id);
        setState({ phase: 'ready', teamId: teams[0].id });
        return;
      }
      const stored = getStoredTeamSelection(role);
      if (stored && teams.some((t) => t.id === stored)) {
        setState({ phase: 'ready', teamId: stored });
        return;
      }
      setState({ phase: 'pick', teams, note: null });
    },
    [role],
  );

  const resolveInitial = useCallback(async () => {
    const run = ++runIdRef.current;
    setState({ phase: 'loading' });
    try {
      const teams = await fetchTeams();
      if (runIdRef.current !== run) return;
      adopt(teams);
    } catch {
      if (runIdRef.current !== run) return;
      setState({ phase: 'error' });
    }
  }, [adopt, fetchTeams]);

  useEffect(() => {
    void resolveInitial();
  }, [resolveInitial]);

  /** Recovery after the server refuses the active selection (see header). */
  const recover = useCallback(async () => {
    const current = stateRef.current;
    if (current.phase !== 'ready' && current.phase !== 'passthrough') return;
    const failedId = current.phase === 'ready' ? current.teamId : getStoredTeamSelection(role);
    const failedTeam = failedId ? teamsRef.current.find((t) => t.id === failedId) : undefined;
    const failedName = failedTeam?.name || 'that team';
    clearStoredTeamSelection(role);
    const run = ++runIdRef.current;
    setState({ phase: 'loading' });
    let teams: EligibleTeam[];
    try {
      teams = await fetchTeams();
    } catch {
      if (runIdRef.current !== run) return;
      setState({ phase: 'error' });
      return;
    }
    if (runIdRef.current !== run) return;
    if (teams.length === 0) {
      if (failedId) notifyToast('warning', `You're no longer a member of ${failedName}.`);
      setState({ phase: 'passthrough' });
      return;
    }
    if (teams.length === 1) {
      if (failedId && teams[0].id === failedId) {
        // The fresh list still calls this team eligible but its data
        // endpoints refuse it — stop rather than loop. A manual
        // re-check re-runs the adoption logic.
        setState({ phase: 'blocked', team: teams[0] });
        return;
      }
      setStoredTeamSelection(role, teams[0].id);
      if (failedId) {
        notifyToast('warning', `You're no longer a member of ${failedName}, so you've been moved to ${teams[0].name || 'your other team'}.`);
      }
      setState({ phase: 'ready', teamId: teams[0].id });
      return;
    }
    setState({
      phase: 'pick',
      teams,
      note: failedId
        ? `You're no longer a member of ${failedName}. Choose a team to continue.`
        : 'Choose a team to continue.',
    });
  }, [fetchTeams, role]);

  const recoverRef = useRef(recover);
  recoverRef.current = recover;
  useEffect(() => {
    const handler = (e: Event) => {
      if ((e as CustomEvent).detail?.role === role) void recoverRef.current();
    };
    window.addEventListener(TEAM_SELECTION_INVALID_EVENT, handler);
    return () => window.removeEventListener(TEAM_SELECTION_INVALID_EVENT, handler);
  }, [role]);

  const handleSelectTeam = useCallback(
    (teamId: string) => {
      setStoredTeamSelection(role, teamId);
      setState({ phase: 'ready', teamId });
    },
    [role],
  );

  /**
   * In-dashboard switcher entry point (Slice 3, plan §(d)): persist the
   * choice and remount the dashboard subtree under the new team key —
   * the previous team's component state is destroyed by the remount, so
   * nothing can bleed across. The switch is a read-side context change
   * only: it writes no memberships and never touches identity or role.
   * Optimistic by design: if the target stopped verifying between the
   * list fetch and the click, the remounted dashboard's first refused
   * request drives recover() above, which lands the user gracefully
   * (moved + toast / picker with a note / blocked panel).
   */
  const switchTeam = useCallback(
    (teamId: string) => {
      const current = stateRef.current;
      if (current.phase !== 'ready' || current.teamId === teamId) return;
      if (!teamsRef.current.some((t) => t.id === teamId)) return;
      setStoredTeamSelection(role, teamId);
      setState({ phase: 'ready', teamId });
    },
    [role],
  );

  if (state.phase === 'loading') return <ShellSkeleton />;

  if (state.phase === 'error') {
    return (
      <div className="min-h-dvh premium-shell flex flex-col items-center justify-center gap-4 p-6" role="alert" aria-live="assertive">
        <div className="premium-loader" />
        <p className="text-sm text-slate-600 text-center max-w-sm">
          We couldn&apos;t load your teams. Please check your connection and try again.
        </p>
        <button
          onClick={() => { void resolveInitial(); }}
          className="px-5 py-2.5 bg-brand-primary hover:bg-brand-primary-dark text-white rounded-xl text-sm font-semibold transition cursor-pointer"
        >
          Retry
        </button>
      </div>
    );
  }

  if (state.phase === 'pick') {
    return (
      <TeamSelectionScreen
        role={role}
        teams={state.teams}
        note={state.note}
        onSelectTeam={handleSelectTeam}
        onLogout={onLogout}
      />
    );
  }

  if (state.phase === 'blocked') {
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center bg-slate-100 p-4 sm:p-6 font-sans text-slate-800">
        <div className="w-full max-w-lg bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 shadow-xl space-y-6 animate-fade-in text-center">
          <div className="space-y-2">
            <div className="mx-auto w-12 h-12 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-500">
              <ShieldAlert className="h-6 w-6" />
            </div>
            <h2 className="text-xl sm:text-2xl font-black font-display text-slate-900 tracking-tight">
              You no longer have access to this team
            </h2>
            <p className="text-xs text-slate-500 max-w-sm mx-auto leading-relaxed">
              {state.team.name || `This ${TEAM_ROLE_LABEL[role]} team`} isn&apos;t available to you
              anymore. Your membership may have been changed by the team&apos;s
              Super Agent or by an administrator.
            </p>
          </div>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => { void resolveInitial(); }}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold transition cursor-pointer"
            >
              Check again
            </button>
            <button
              onClick={onLogout}
              className="flex items-center gap-1.5 px-4 py-2.5 text-rose-600 hover:text-rose-500 text-sm font-bold transition cursor-pointer"
            >
              <LogOut className="h-3.5 w-3.5" />
              Sign Out
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (state.phase === 'ready') {
    // Remount-by-key: a team change destroys the previous team's
    // dashboard state by construction — nothing can bleed across.
    return (
      <TeamSelectionContext.Provider
        value={{ role, teams: teamsRef.current, activeTeamId: state.teamId, switchTeam }}
      >
        <React.Fragment key={state.teamId}>{children}</React.Fragment>
      </TeamSelectionContext.Provider>
    );
  }

  // passthrough — zero eligible teams confirmed: legacy behavior, untouched.
  // The context still reports the (empty) resolution so the dashboards'
  // TeamSwitcher renders nothing rather than a stale or guessed team.
  return (
    <TeamSelectionContext.Provider
      value={{ role, teams: [], activeTeamId: null, switchTeam }}
    >
      <>{children}</>
    </TeamSelectionContext.Provider>
  );
}
